import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Swimmer } from "../../../../api/client/types/swimmer.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { PaginatedTable } from "../../../../shared/components/ui/PaginatedTable";
import { type ColumnDef } from "@tanstack/react-table";
import { ROUTES } from "../../../../routes/routes";
import { Newspaper, Pencil, UserPlus } from "lucide-react";
import { IconActionButton } from "../../../../shared/components/ui/IconActionButton";
import { getAgeClassificationDisplayLabel } from "../../utils/ageClassificationUtils";

interface SwimmerTableProps {
  swimmers: PaginatedResponse<Swimmer>;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  todayCount?: number;
  /** When provided, shows an "Editar" action (e.g. for lifeguard dashboard). */
  onEditSwimmer?: (swimmer: Swimmer) => void;
}

export const SwimmerTable: React.FC<SwimmerTableProps> = ({
  swimmers,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  todayCount = 0,
  onEditSwimmer,
}) => {
  const navigate = useNavigate();

  const handleAddToGroup = useCallback(
    (swimmer: Swimmer) => {
      navigate(ROUTES.SWIMMERS.REGISTER_WITH_SWIMMER(swimmer.id));
    },
    [navigate]
  );

  const handleAddNewToSwimmer = useCallback(
    (swimmer: Swimmer) => {
      navigate(ROUTES.NEWS.REGISTER_WITH_SWIMMER(swimmer.id));
    },
    [navigate]
  );

  const columns = React.useMemo<ColumnDef<Swimmer>[]>(
    () => [
      {
        header: "Acciones",
        accessorKey: "actions",
        cell: ({ row }) => (
          <div className="flex flex-row items-center gap-3">
            {onEditSwimmer && (
              <div className="flex items-center justify-start cursor-pointer">
                <IconActionButton
                  icon={Pencil}
                  onClick={() => onEditSwimmer(row.original)}
                  title="Editar bañista"
                />
              </div>
            )}
            <div className="flex items-center justify-start cursor-pointer">
              <IconActionButton
                icon={UserPlus}
                onClick={() => handleAddToGroup(row.original)}
                title="Agregar bañista al grupo"
                disabled={
                  row.original.age_classification?.name === "Menor de 12 años"
                }
              />
            </div>
            <div className="flex items-center justify-start cursor-pointer">
              <IconActionButton
                icon={Newspaper}
                onClick={() => handleAddNewToSwimmer(row.original)}
                title="Reportar novedad"
              />
            </div>
          </div>
        ),
      },
      {
        header: "Nombre",
        accessorKey: "name",
        cell: ({ getValue }) => (
          <div className="line-clamp-3" title={getValue() as string}>
            {getValue() as string}
          </div>
        ),
      },
      {
        header: "T-APTO",
        accessorKey: "building_tower",
        cell: ({ row }) => {
          const tower = row.original.building_tower || "";
          const apartment = row.original.building_apartment || "";
          const value = `${tower}-${apartment}`;
          return (
            <div className="line-clamp-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        header: "Edad",
        accessorKey: "age_classification.name",
        cell: ({ row }) => {
          const originalValue = row.original.age_classification?.name || "—";
          const displayValue =
            originalValue !== "—"
              ? getAgeClassificationDisplayLabel(originalValue)
              : originalValue;
          return (
            <div className="line-clamp-3" title={originalValue}>
              {displayValue}
            </div>
          );
        },
      },
      {
        header: "Tipo",
        accessorKey: "swimmer_type.name",
        cell: ({ row }) => {
          const value = row.original.swimmer_type?.name || "—";
          return (
            <div className="line-clamp-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        header: "Responsable",
        accessorKey: "responsible.name",
        cell: ({ row }) => {
          const value = row.original.responsible?.name || "N/A";
          return (
            <div className="line-clamp-3" title={value}>
              {value}
            </div>
          );
        },
      },
      {
        header: "Registrado el",
        accessorKey: "registration_timestamp",
        cell: ({ getValue }) => {
          const date = new Date(getValue() as string);
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}/${month}/${year}`;
        },
      },
    ],
    [handleAddToGroup, handleAddNewToSwimmer, onEditSwimmer]
  );

  // Use the todayCount prop from the API instead of calculating it client-side
  const isPlural = todayCount > 1 || todayCount === 0;
  return (
    <PaginatedTable<Swimmer>
      data={swimmers}
      columns={columns}
      isLoading={isLoading}
      isError={isError}
      page={page}
      pageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      totalLabel="Total del día"
      entityName={`usuario${isPlural ? "s" : ""} registrado${
        isPlural ? "s" : ""
      }`}
      customTotal={todayCount}
      rowClassName="h-12"
    />
  );
};
